import { CookieId, useIsCookieAccepted, useLocalize } from "@faro-lotv/foreign-observers";
import { useStellarApiClient } from "@hooks/use-stellar-api-client";
import { useAppParams } from "@router/router-helper";
import { runtimeConfig } from "@src/runtime-config";
import { sentryCaptureError } from "@src/utils/sentry-utils";
import { sdbCompaniesSelector, selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { LocalStorageUtils } from "@utils/local-storage-utils";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getPathnameFromUrl, getQueryParamsFromSearch } from "@utils/url-utils";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * No matter the dependencies it seems that the useEffect to call the init route
 * is called twice, so it stores similar to the isApiInitialized state, whether
 * initialized was already called.
 */
let isApiInitializedLocally = false;

/**
 * Hook to initialize the user in the dashboard 2.0 API
 */
export function useTrackPageLoad() {
  const [isApiInitialized, setIsApiInitialized] = useState(false);
  const routeLocation = useLocation();
  const {
    trackAsyncEvent
  } = useTrackEvent();
  const selectedSdbCompany = useAppSelector(selectedSdbCompanySelector);
  const sdbCompanies = useAppSelector(sdbCompaniesSelector);
  const appParams = useAppParams();
  const currentUser = useAppSelector(currentUserSelector);
  const {
    stellarApiClient
  } = useStellarApiClient();
  const {
    currentLanguage
  } = useLocalize();
  const isCookieAccepted = useIsCookieAccepted();
  const {
    companyId,
    projectId
  } = useAppParams();

  // Call the routes to initialize the user in the dashboard 2.0 API
  // This route will take care of tracking the page load in Amplitude for all users.
  useEffect(() => {
    async function trackPageLoaded(currentUser_0) {
      const urlPathname = getPathnameFromUrl(routeLocation.pathname, appParams);
      const urlQuery = getQueryParamsFromSearch(routeLocation.search);
      const deviceSizes = {
        screenWidth: window.screen.width,
        windowWidth: window.innerWidth,
        screenHeight: window.screen.height,
        windowHeight: window.innerHeight,
        windowOrientation: window.screen.orientation?.type
      };
      try {
        const appInitTime = LocalStorageUtils.getStringItem("app-init-time") ?? undefined;
        const response = await stellarApiClient.init.postInit({
          version: runtimeConfig.appVersion,
          application: "Sphere XG Dashboard",
          userId: currentUser_0.identity,
          userEmail: currentUser_0.email,
          appInitTime,
          isConsentGiven: isCookieAccepted(CookieId.amplitude) ?? false,
          language: currentLanguage.code,
          workspaces: sdbCompanies.length,
          workspaceRole: selectedSdbCompany?.role.company ?? "Unknown",
          workspaceTags: selectedSdbCompany?.tags ?? [],
          workspaceId: companyId,
          projectId,
          urlPathname,
          urlQuery,
          ...deviceSizes
        });
        if (response.success) {
          LocalStorageUtils.setStringItem("app-init-time", Date.now().toString());
        }
      } catch (error) {
        // Failing to call the init route should not block the user from using the app
        // Use Sentry to log the error
        sentryCaptureError({
          error,
          title: "Error calling init route"
        });
      } finally {
        await trackAsyncEvent({
          name: WorkspaceEvents.pageLoaded,
          props: {
            ...deviceSizes,
            language: currentLanguage.code
          }
        });
      }
    }
    if (selectedSdbCompany && sdbCompanies.length > 0 && currentUser && !isApiInitialized && !isApiInitializedLocally && routeLocation?.pathname && appParams) {
      isApiInitializedLocally = true;
      // Call this upfront to avoid multiple calls while the promise is being resolved
      setIsApiInitialized(true);
      void trackPageLoaded(currentUser);
    }
  }, [appParams, currentLanguage.code, currentUser, isApiInitialized, stellarApiClient, routeLocation.pathname, routeLocation.search, sdbCompanies.length, selectedSdbCompany, companyId, projectId, trackAsyncEvent, isCookieAccepted]);
}